import * as React from "react";
import { useLocation } from "@tanstack/react-router";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PhoneInput, validateE164 } from "@/components/PhoneInput";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useLocaleSafe } from "@/contexts/LocaleContext";
import { t as translate, type Locale } from "@/lib/i18n";

import { toast } from "@/lib/toast";
import { Phone, Shield } from "lucide-react";

const DISMISS_KEY = "lecmo:phone_gate_dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * بوّابة طلب الهاتف: تظهر عند دخول /account للمستخدم بلا phone_e164،
 * لكنّها قابلة للتجاهل لمدّة 24 ساعة. جميع نصوصها قابلة للتخصيص من
 * /admin/phone-messages عبر مفاتيح `phone.*`.
 */
export function RequirePhoneGate() {
  const { locale } = useLocaleSafe();
  const { pathname } = useLocation();
  const isRtl = locale === "ar";
  const tt = (key: string, vars?: Record<string, string>) => translate(locale, key, vars);

  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // البوّابة تعتمد على localStorage + auth context؛ لا حاجة لتصييرها على السيرفر،
  // وتجنّب استدعاء useAuth أثناء SSR يمنع قفزة السياق عند حالات edge.
  if (!mounted || typeof window === "undefined") return null;

  return <RequirePhoneGateClient tt={tt} isRtl={isRtl} pathname={pathname} locale={locale} />;
}

function RequirePhoneGateClient({
  tt,
  isRtl,
  pathname,
  locale,
}: {
  tt: (key: string, vars?: Record<string, string>) => string;
  isRtl: boolean;
  pathname: string;
  locale: Locale;
}) {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading, update, refresh } = useUserProfile();

  const [phone, setPhone] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    const at = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    return at > 0 && Date.now() - at < DISMISS_TTL_MS;
  });

  const onAccount = pathname.startsWith("/account");

  const needsPhone =
    !!user &&
    !authLoading &&
    !profileLoading &&
    profile !== null &&
    !profile.phone_e164;

  const open = needsPhone && onAccount && !dismissed;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* تجاهل */
    }
    setDismissed(true);
  };

  const handleSave = async () => {
    const e164 = phone.trim();
    const validationError = validateE164(e164, locale);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setBusy(true);
    try {
      await update({ phone_e164: e164 });
      await refresh();
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch {
        /* تجاهل */
      }
      toast.success(tt("phone.gate.success"));
    } catch (err) {
      if (err instanceof Error && err.message === "PHONE_ALREADY_REGISTERED") {
        toast.error(tt("phone.gate.error_already_linked"));
      } else {
        console.error("RequirePhoneGate save failed:", err);
        toast.error(tt("phone.gate.error_save_failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md" dir={isRtl ? "rtl" : "ltr"}>
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Phone className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">
            {tt("phone.gate.title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {tt("phone.gate.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <PhoneInput value={phone} onChange={setPhone} />

          <div className="flex items-start gap-2 rounded-none bg-muted/50 p-3 text-xs text-muted-foreground">
            <Shield className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>{tt("phone.gate.privacy")}</span>
          </div>

          <Button
            onClick={handleSave}
            disabled={busy || !phone.trim()}
            className="w-full"
            size="lg"
          >
            {busy ? tt("phone.gate.cta_saving") : tt("phone.gate.cta_save")}
          </Button>

          <button
            type="button"
            onClick={handleDismiss}
            className="block w-full text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {tt("phone.gate.cta_later")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
